<script setup lang="ts">
import { PropType, computed } from 'vue'
import { LevelGroup, SkillOption } from '@/presentlevels/presentlevels_types.ts'
import { usePresentLevelsStore } from "@/presentlevels/presentlevels.store.ts";
import { storeToRefs } from "pinia";
import Checkbox from 'primevue/checkbox'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

const presentLevelsStore = usePresentLevelsStore()
const { selectedSkillsMap } = storeToRefs(presentLevelsStore)

const props = defineProps({
  levelGroup: {
    type: Object as PropType<LevelGroup>,
    required: true
  }
})

// Show a grade-level reminder only on the section that includes the student's
// enrolled grade. For banded high school (e.g. 11th-12th) this is the band that
// contains the enrolled grade — the top relevant section — rather than every
// 9th-12th band. If the enrolled grade is outside every group's range (above a
// category's ceiling or below its floor) no section contains it, so nothing shows.
const atEnrolledGradeLevel = computed<boolean>(() => {
  const enrolledGrade = presentLevelsStore.enrolledGrade
  if (!enrolledGrade) return false
  return props.levelGroup.levels.some(level => level.order === enrolledGrade.order)
})

function formatGradeLabel(label: string): string {
  // Check if it's a multi-grade label (e.g., "9th - 10th")
  const isMultiGradeRange = /^(\d+)(st|nd|rd|th)\s*-\s*(\d+)(st|nd|rd|th)$/i.test(label);

  // First uppercase everything
  let result = label.toUpperCase();

  // Then find and lowercase all ordinal suffixes (st, nd, rd, th)
  result = result.replace(/(\d+)(ST|ND|RD|TH)/g, (match, number, ordinal) => {
    return number + ordinal.toLowerCase();
  });

  // Add "GRADE" to multi-grade labels if it's not already there
  if (isMultiGradeRange && !result.includes('GRADE')) {
    result += ' GRADE';
  }

  return result;
}

const analytics = getAnalyticsTracker().getChildContext({
  plw_skill_level: props.levelGroup.label
})

function updateSkillStatus(skill: SkillOption, status: string | null) {
  selectedSkillsMap.value.strengths[skill.uuid] = false
  selectedSkillsMap.value.needs[skill.uuid] = false
  if (status === 'strength') {
    selectedSkillsMap.value.strengths[skill.uuid] = true
  } else if (status === 'need') {
    selectedSkillsMap.value.needs[skill.uuid] = true
  }

  if (status) {
    analytics?.sendEvent(EVENT_NAMES.PRESENTLEVELS.SKILL_SELECTION.CLICKED_SKILL, {
      plw_skill_text: skill.label,
      plw_skill_strength_need: status
    })
  }
}
</script>

<template>
  <div>
    <div class="grid grid-cols-[auto_auto_1fr] gap-y-2">
      <span class="col-span-1 text-default text-sm font-medium">STRENGTH</span>
      <span class="col-span-1 text-default text-sm font-medium ml-6">NEED</span>
      <div class="col-span-1 ml-16 flex flex-wrap items-center gap-2">
        <span class="text-default text-sm font-bold">{{ formatGradeLabel(props.levelGroup.label) }}</span>
        <span
          v-if="atEnrolledGradeLevel"
          class="inline-flex items-center gap-1 rounded-full bg-blue-200 px-3 py-1 text-sm font-bold text-blue-500"
        >
          <span class="material-icons-round text-base" aria-hidden="true">flag</span>
          Select at least one need at grade level
        </span>
      </div>
      <div class="col-span-3 border-b border-gray-700 mb-2"></div>

      <template v-for="skill in props.levelGroup.skills" :key="skill.uuid">
        <div class="col-span-1 py-1 flex items-center justify-center">
          <div class="relative flex items-center justify-center">
            <Checkbox
              :modelValue="selectedSkillsMap.strengths[skill.uuid]"
              :binary="true"
              :inputId="`${skill.uuid}-strength`"
              @update:modelValue="updateSkillStatus(skill, $event ? 'strength' : null)"
              gbTheme="green"
              class="strength-checkbox !w-6 !h-6 [&>div]:!rounded-full [&>div]:!w-6 [&>div]:!h-6"
            />
            <label :for="`${skill.uuid}-strength`" class="sr-only">{{ skill.label }} - Strength</label>
          </div>
        </div>
        <div class="col-span-1 py-1 flex items-center justify-center ml-6">
          <div class="relative flex items-center justify-center">
            <Checkbox
              :modelValue="selectedSkillsMap.needs[skill.uuid]"
              :binary="true"
              :inputId="`${skill.uuid}-need`"
              @update:modelValue="updateSkillStatus(skill, $event ? 'need' : null)"
              gbTheme="blue"
              class="need-checkbox !w-6 !h-6 [&>div]:!rounded-full [&>div]:!w-6 [&>div]:!h-6"
            />
            <label :for="`${skill.uuid}-need`" class="sr-only">{{ skill.label }} - Need</label>
          </div>
        </div>
        <div class="col-span-1 py-1 ml-16" :id="`skill-${skill.uuid}-label`">{{ skill.label }}</div>
      </template>
    </div>
  </div>
</template>